// Single source of truth for "when did we last hear from the subscription transport". Updated by the
// WebSocket tap on every inbound AppSync message (ka keepalive OR data frame), read by the liveness
// watchdog. ka flows even when the fridge is idle, so this stays fresh on a healthy-but-quiet stream
// and only goes stale when the socket is actually dead.
let lastInboundAt = Date.now();

const inboundListeners = new Set<() => void>();

export const markSubscriptionInbound = (): void => {
	lastInboundAt = Date.now();
	for (const listener of inboundListeners) {
		listener();
	}
};

export const getLastInboundAt = (): number => lastInboundAt;

// Re-baseline after a (re)connect so the gap before the first ka/frame can't be mistaken for staleness.
export const resetSubscriptionLiveness = (): void => {
	lastInboundAt = Date.now();
};

// Subscribe to inbound transport activity (ka, connection_ack, or data frame). Returns an unsubscribe.
// Lets recovery clear a broken-stream flag the moment delivery resumes, instead of waiting for a data
// frame that an idle fridge may never send.
export const onSubscriptionInbound = (listener: () => void): (() => void) => {
	inboundListeners.add(listener);
	return () => {
		inboundListeners.delete(listener);
	};
};

export type LivenessTickAction = "rebaseline" | "trip" | "healthy";

/**
 * Decide what a watchdog tick should do, given wall-clock `now`, the previous tick time, the last inbound
 * time, and the staleness threshold.
 *
 * `rebaseline` guards against an iOS WKWebView suspend/resume: when the kiosk sleeps, the interval pauses
 * and `Date.now()` jumps, so the first tick on resume sees a gap since the previous tick far larger than
 * the (≤ threshold/4) cadence. That's a clock jump, not a quiet stream — re-baseline rather than trip a
 * false reload. A genuinely dead socket still trips on the NEXT window. (The transport's own
 * onComplete/onError handles the common dead-on-resume case faster anyway; this is the silent-hang backstop.)
 */
export const evaluateLivenessTick = (
	now: number,
	lastTickAt: number,
	lastInboundTime: number,
	staleThresholdMs: number,
): LivenessTickAction => {
	if (now - lastTickAt > staleThresholdMs) {
		return "rebaseline";
	}
	if (now - lastInboundTime >= staleThresholdMs) {
		return "trip";
	}
	return "healthy";
};
